import { FormlyFieldConfig } from '@ngx-formly/core';
import { EFieldValidationPatternConfigOptions } from '../../../models/form-fields-formly-validation.model';
import { FormBuilderService } from '../../../services/form-builder.service';
import {
  EIremboFormlyFieldTypes,
  EIremboFormlyValidationTypes,
} from '@irembo-andela/irembogov3-common';
import { AbstractControl } from '@angular/forms';
import { filter, tap } from 'rxjs';
import { AttachmentFieldFileFormat } from '../../../constants/attachment-field-file-format-emum-to-types.enum';
import { generateDatatimePickerValidationMessages } from '../form-field-formly-types-main-configurations/custom-datetimepicker-field.configuration';

export const getRequiredValidatorFormConfig = (): FormlyFieldConfig => {
  return {
    key: 'required',
    type: EIremboFormlyFieldTypes.checkbox,
    defaultValue: false,
    props: {
      formCheck: 'switch',
      label: 'Required',
      placeholder: 'Required Field',
      required: false,
    },
  };
};

export const getIdConditionsValidatorFormConfig = (): FormlyFieldConfig => {
  return {
    key: 'idConditions',
    fieldGroup: [getIdAgeLimitValidationFIeld()],
  };
};

const getIdAgeLimitValidationFIeld = (): FormlyFieldConfig => {
  const fieldGroup: FormlyFieldConfig[] = [
    {
      key: 'key',
      type: EIremboFormlyFieldTypes.input,
      defaultValue: 'dateOfBirth',
      props: {
        hideField: true,
        label: 'Required',
        placeholder: 'Required Field',
        required: false,
      },
    },
    {
      key: 'minimumAge',
      type: EIremboFormlyFieldTypes.input,
      props: {
        type: 'number',
        label: 'Minimum Age',
        placeholder: 'Minimum Age',
        required: false,
      },
      validation: {
        messages: {
          max: 'Value can not be more than maximum age',
        },
      },
      expressions: {
        'props.max': 'model?.maximumAge ? Number(model.maximumAge) : undefined',
      },
    },
    {
      key: 'maximumAge',
      type: EIremboFormlyFieldTypes.input,
      props: {
        type: 'number',
        label: 'Maximum Age',
        placeholder: 'Maximum Age',
        required: false,
      },
      validation: {
        messages: {
          min: 'Value can not be more than minimum age',
        },
      },
      expressions: {
        'props.min': 'model?.minimumAge ? Number(model.minimumAge) : undefined',
      },
    },
  ];

  return {
    key: 'ageLimit',
    type: EIremboFormlyFieldTypes['formly-group'],
    props: {
      label: 'Age Limit',
    },
    fieldGroup,
  };
};
export const getMinValidatorFormConfig = (): FormlyFieldConfig => {
  return {
    key: 'min',
    type: EIremboFormlyFieldTypes.input,
    props: {
      type: 'number',
      label: 'Minimum number:',
      placeholder: 'Enter Min number',
      required: false,
      min: 0,
    },
    parsers: [Number],
  };
};

export const getMaxValidatorFormConfig = (): FormlyFieldConfig => {
  return {
    key: 'max',
    type: EIremboFormlyFieldTypes.input,
    props: {
      type: 'number',
      label: 'Maximum number:',
      placeholder: 'Enter Maximum number',
      required: false,
      min: 0,
    },
    parsers: [Number],
  };
};

export const getMinLengthValidatorFormConfig = (): FormlyFieldConfig => {
  return {
    key: 'minLength',
    type: EIremboFormlyFieldTypes.input,
    props: {
      type: 'number',
      label: 'Min Character Length:',
      placeholder: 'Enter Min Character Length',
      required: false,
      min: 0,
    },
    parsers: [Number],
  };
};

export const getMaxLengthValidatorFormConfig = (): FormlyFieldConfig => {
  return {
    key: 'maxLength',
    type: EIremboFormlyFieldTypes.input,
    props: {
      type: 'number',
      label: 'Max Character Length:',
      placeholder: 'Enter Max Character Length',
      required: false,
      min: 0,
    },
    parsers: [Number],
  };
};

export const getPatternValidatorFormConfig = (): FormlyFieldConfig => {
  return {
    key: 'patternValidation',
    type: EIremboFormlyFieldTypes['formly-group'],
    fieldGroup: [
      {
        key: 'patternType',
        type: EIremboFormlyFieldTypes.customdropdown,
        props: {
          placeholder: 'Pattern',
          label: 'Pattern',
          options: [
            { label: 'None', value: null },
            {
              label: 'Letters a-z',
              value: EFieldValidationPatternConfigOptions.LETTERS_A_TO_Z,
            },
            {
              label: 'Digit 0-9',
              value: EFieldValidationPatternConfigOptions.NUMBERS_0_TO_9,
            },
            {
              label: 'Symbols',
              value: EFieldValidationPatternConfigOptions.SYMBOLS,
            },
            {
              label: 'Letters & numbers',
              value: EFieldValidationPatternConfigOptions.LETTERS_AND_NUMBERS,
            },
            {
              label: 'Letters & numbers & symbols',
              value:
                EFieldValidationPatternConfigOptions.LETTERS_AND_NUMBERS_AND_SYMBOLS,
            },
            {
              label: 'Email',
              value: EFieldValidationPatternConfigOptions.EMAIL,
            },
            {
              label: 'No Empty Spaces Only',
              value: EFieldValidationPatternConfigOptions.NO_EMPTY_SPACES_ONLY,
            },
            {
              label: 'Custom',
              value: EFieldValidationPatternConfigOptions.CUSTOM,
            },
          ],
          bindLabel: 'label',
          bindValue: 'value',
          required: false,
        },
      },
      {
        key: 'customPattern',
        type: EIremboFormlyFieldTypes.input,
        props: {
          label: 'Custom Pattern',
          placeholder: 'Enter custom Regex pattern',
          required: false,
        },
        expressions: {
          'props.hideField': (field: FormlyFieldConfig) => {
            return (
              field.model?.patternType !==
              EFieldValidationPatternConfigOptions.CUSTOM
            );
          },
          'props.required':
            "model?.patternType === 'Custom' && !model?.customPattern",
        },
      },
    ],
  };
};

export const getUniqueFieldValidatorFormConfig = (
  formBuilderService: FormBuilderService
): FormlyFieldConfig => {
  return {
    key: 'uniqueField',
    type: EIremboFormlyFieldTypes.customdropdown,
    hide: true,
    props: {
      disabled: true,
      placeholder: 'Unique To Fields',
      label: 'Unique To Fields',
      options: formBuilderService.getAllFormFieldLabelAndDetails(true, true),
      multiple: true,
      bindLabel: 'label',
      bindValue: 'value',
      required: false,
    },
  };
};

export const getValidationFieldsFormConfig = (
  formBuilderService: FormBuilderService
): FormlyFieldConfig => {
  return {
    key: 'validationMessages',
    type: EIremboFormlyFieldTypes.block,
    className: 'validation-messages-list-block',
    props: {
      label: 'Error Messages',
      dragDisabled: true,
    },
    fieldGroup: [
      {
        key: EIremboFormlyValidationTypes.required,
        type: EIremboFormlyFieldTypes.input,
        props: {
          placeholder: 'Enter required validation error message',
          label: 'Required Message:',
          required: false,
        },
        expressions: {
          'props.hideField': (field: FormlyFieldConfig) => {
            return !(
              field.parent?.parent?.model?.['required'] ||
              field.parent?.parent?.model?.['conditionalValidations']?.[
                'required'
              ]?.['conditions']
            );
          },
          'props.required': (field: FormlyFieldConfig) => {
            return (
              field.parent?.parent?.model?.['required']?.['conditions'] ||
              field.parent?.parent?.model?.['conditionalValidations']?.[
                'required'
              ]?.['conditions']
            );
          },
        },
      },
      {
        key: EIremboFormlyValidationTypes.min,
        type: EIremboFormlyFieldTypes.input,
        props: {
          label: 'Minimum Number Message:',
          required: false,
          placeholder: 'Enter Minimum Number Error Message',
        },
        expressions: {
          'props.required': (field: FormlyFieldConfig) => {
            return field.parent?.parent?.model?.['min'];
          },
          'props.hideField': (field: FormlyFieldConfig) => {
            return !field.parent?.parent?.model['min'];
          },
        },
      },
      {
        key: EIremboFormlyValidationTypes.max,
        type: EIremboFormlyFieldTypes.input,
        props: {
          label: 'Maximum Number Message:',
          placeholder: 'Enter Maximum number Error Message',
          required: false,
        },
        expressions: {
          'props.hideField': (field: FormlyFieldConfig) => {
            return !field.parent?.parent?.model['max'];
          },
          'props.required': (field: FormlyFieldConfig) => {
            return field.parent?.parent?.model?.['max'];
          },
        },
      },
      {
        key: EIremboFormlyValidationTypes.minimumAge,
        type: EIremboFormlyFieldTypes.input,
        props: {
          placeholder: 'Enter Minimum Age validation error message',
          label: 'Minimum Age Message:',
          required: false,
        },
        expressions: {
          hide: (field: FormlyFieldConfig) => {
            return !field.parent?.parent?.model?.['idConditions']?.[
              'ageLimit'
            ]?.['minimumAge'];
          },
          'props.required': (field: FormlyFieldConfig) => {
            return field.parent?.parent?.model?.['idConditions']?.[
              'ageLimit'
            ]?.['minimumAge'];
          },
        },
      },
      {
        key: EIremboFormlyValidationTypes.maximumAge,
        type: EIremboFormlyFieldTypes.input,
        props: {
          placeholder: 'Enter Maximum Age validation error message',
          label: 'Maximum Age Message:',
          required: false,
        },
        expressions: {
          hide: (field: FormlyFieldConfig) => {
            return !field.parent?.parent?.model?.['idConditions']?.[
              'ageLimit'
            ]?.['maximumAge'];
          },
          'props.required': (field: FormlyFieldConfig) => {
            return field.parent?.parent?.model?.['idConditions']?.[
              'ageLimit'
            ]?.['maximumAge'];
          },
        },
      },
      {
        key: EIremboFormlyValidationTypes.minLength,
        type: EIremboFormlyFieldTypes.input,
        props: {
          label: 'Min Length Message:',
          required: false,
          placeholder: 'Enter Min Length Error Message',
        },
        expressions: {
          'props.required': (field: FormlyFieldConfig) => {
            return (
              field.parent?.parent?.model?.['minLength'] ||
              field.parent?.parent?.model?.['conditionalValidations']?.[
                'minLength'
              ]?.[0]
            );
          },
          'props.hideField': (field: FormlyFieldConfig) => {
            return !(
              field.parent?.parent?.model['minLength'] ||
              field.parent?.parent?.model?.['conditionalValidations']?.[
                'minLength'
              ]?.[0]
            );
          },
        },
      },
      {
        key: EIremboFormlyValidationTypes.maxLength,
        type: EIremboFormlyFieldTypes.input,
        props: {
          label: 'Max Length Message:',
          placeholder: 'Enter Max Length Error Message',
          required: false,
        },
        expressions: {
          'props.hideField': (field: FormlyFieldConfig) => {
            return !(
              field.parent?.parent?.model['maxLength'] ||
              field.parent?.parent?.model?.['conditionalValidations']?.[
                'maxLength'
              ]?.[0]
            );
          },
          'props.required': (field: FormlyFieldConfig) => {
            return (
              field.parent?.parent?.model?.['maxLength'] ||
              field.parent?.parent?.model?.['conditionalValidations']?.[
                'maxLength'
              ]?.[0]
            );
          },
        },
      },
      {
        key: EIremboFormlyValidationTypes.pattern,
        type: EIremboFormlyFieldTypes.input,
        props: {
          label: 'Pattern:',
          placeholder: 'Enter pattern validation error message',
          required: false,
        },
        expressions: {
          'props.hideField': (field: FormlyFieldConfig) => {
            return !(
              field.parent?.parent?.model?.['patternValidation']?.[
                'patternType'
              ] ||
              field.parent?.parent?.model?.['conditionalValidations']?.[
                'patternValidation'
              ]?.[0]
            );
          },
          'props.required': (field: FormlyFieldConfig) => {
            return (
              field.parent?.parent?.model?.['patternType'] ||
              field.parent?.parent?.model?.['conditionalValidations']?.[
                'patternValidation'
              ]?.[0]
            );
          },
        },
      },
      {
        key: EIremboFormlyValidationTypes.uniqueField,
        type: EIremboFormlyFieldTypes.input,
        props: {
          label: 'Unique Field:',
          placeholder: 'Enter unique validation error message',
          required: false,
        },
        expressions: {
          'props.hideField': (field: FormlyFieldConfig) => {
            return (
              !field.parent?.parent?.model['uniqueField'] ||
              field.parent?.parent?.model['uniqueField'].length < 1
            );
          },
          'props.required': (field: FormlyFieldConfig) => {
            return (
              field.parent?.parent?.model['uniqueField'] &&
              field.parent?.parent?.model['uniqueField'].length > 0
            );
          },
        },
      },
      {
        key: EIremboFormlyValidationTypes.invalidInput,
        type: EIremboFormlyFieldTypes.input,
        props: {
          label: 'Invalid Input:',
          placeholder: 'Enter invalid Input message',
          required: false,
        },
        expressions: {
          'props.hideField': () => {
            const fieldType =
              formBuilderService.configureFieldAction$.getValue()?.type;
            return fieldType
              ? ![
                  EIremboFormlyFieldTypes.customidinput,
                  EIremboFormlyFieldTypes.customtininput,
                ].includes(fieldType as EIremboFormlyFieldTypes)
              : false;
          },
          'props.required': () => {
            return (
              formBuilderService.configureFieldAction$.getValue()?.type ===
              EIremboFormlyFieldTypes.customidinput
            );
          },
        },
      },
      {
        key: EIremboFormlyValidationTypes.invalidId,
        type: EIremboFormlyFieldTypes.input,
        props: {
          label: 'Invalid Id:',
          placeholder: 'Enter invalid Id message',
          required: false,
        },
        expressions: {
          'props.hideField': () => {
            return (
              formBuilderService.configureFieldAction$.getValue()?.type !==
              EIremboFormlyFieldTypes.customidinput
            );
          },
          'props.required': () => {
            return (
              formBuilderService.configureFieldAction$.getValue()?.type ===
              EIremboFormlyFieldTypes.customidinput
            );
          },
        },
      },
      {
        key: EIremboFormlyValidationTypes.minimumUploadSize,
        type: EIremboFormlyFieldTypes.input,
        props: {
          label: 'Minimum file size:',
          placeholder: 'Minimum File Size error message',
          required: false,
        },
        expressions: {
          'props.hideField': (field: FormlyFieldConfig) => {
            return !field.parent?.parent?.model?.['fileSize']?.[
              'minimumUploadSize'
            ];
          },
          'props.required': (field: FormlyFieldConfig) => {
            return field.parent?.parent?.model?.['fileSize']?.[
              'minimumUploadSize'
            ];
          },
        },
      },
      {
        key: EIremboFormlyValidationTypes.maximumUploadSize,
        type: EIremboFormlyFieldTypes.input,
        props: {
          label: 'Maximum file size:',
          placeholder: 'Maximum File Size error message',
          required: false,
        },
        expressions: {
          'props.hideField': (field: FormlyFieldConfig) => {
            return !field.parent?.parent?.model?.['fileSize']?.[
              'maximumUploadSize'
            ];
          },
          'props.required': (field: FormlyFieldConfig) => {
            return field.parent?.parent?.model?.['fileSize']?.[
              'maximumUploadSize'
            ];
          },
        },
      },
      {
        key: EIremboFormlyValidationTypes.invalidfileformat,
        type: EIremboFormlyFieldTypes.input,
        props: {
          label: 'Allowed file formats:',
          placeholder: 'Allowed file formats error message',
          required: false,
        },
        expressions: {
          'props.hideField': (field: FormlyFieldConfig) => {
            return !(
              field.parent?.parent?.model?.['fileFormats'] &&
              Object.keys(field.parent?.parent?.model?.['fileFormats']).some(
                x => field.parent?.parent?.model?.['fileFormats'][x]
              )
            );
          },
          'props.required': (field: FormlyFieldConfig) => {
            return (
              field.parent?.parent?.model?.['fileFormats'] &&
              Object.keys(field.parent?.parent?.model?.['fileFormats']).some(
                x => field.parent?.parent?.model?.['fileFormats'][x]
              )
            );
          },
        },
      },
      {
        key: EIremboFormlyValidationTypes.invalidNumberFormat,
        type: EIremboFormlyFieldTypes.input,
        props: {
          placeholder: 'Number format is invalid',
          label: 'Invalid Phone Number Format Message:',
          required: false,
        },
        expressions: {
          'props.hideField': () => {
            const configuredField: FormlyFieldConfig | null =
              formBuilderService.configureFieldAction$.getValue();
            return (
              configuredField?.type !==
              EIremboFormlyFieldTypes.customphonenumber
            );
          },
          'props.required': () => {
            const configuredField: FormlyFieldConfig | null =
              formBuilderService.configureFieldAction$.getValue();
            return (
              configuredField?.type ===
              EIremboFormlyFieldTypes.customphonenumber
            );
          },
        },
      },
      {
        key: EIremboFormlyValidationTypes.invalidNumber,
        type: EIremboFormlyFieldTypes.input,
        props: {
          placeholder: 'Provided number is invalid',
          label: 'Invalid Phone Number Message:',
          required: false,
        },
        expressions: {
          'props.hideField': () => {
            const configuredField: FormlyFieldConfig | null =
              formBuilderService.configureFieldAction$.getValue();
            return (
              configuredField?.type !==
              EIremboFormlyFieldTypes.customphonenumber
            );
          },
          'props.required': () => {
            const configuredField: FormlyFieldConfig | null =
              formBuilderService.configureFieldAction$.getValue();
            return (
              configuredField?.type ===
              EIremboFormlyFieldTypes.customphonenumber
            );
          },
        },
      },
      {
        key: EIremboFormlyValidationTypes.uniqueField,
        type: EIremboFormlyFieldTypes.input,
        props: {
          placeholder: 'Enter unique validation error message',
          label: 'Unique Message:',
          required: false,
        },
        expressions: {
          'props.hideField': (field: FormlyFieldConfig) => {
            return !field.parent?.parent?.model['unique'];
          },
          'props.uniqueField': (field: FormlyFieldConfig) => {
            return field.parent?.parent?.model?.['unique'];
          },
        },
      },
      {
        key: EIremboFormlyValidationTypes.invalidTIN,
        type: EIremboFormlyFieldTypes.input,
        props: {
          placeholder: 'Enter Invalid TIN error message',
          label: 'Invalid Tin Message:',
          required: false,
        },
        expressions: {
          'props.hideField': () => {
            return (
              formBuilderService.configureFieldAction$.getValue()?.type !==
              EIremboFormlyFieldTypes.customtininput
            );
          },
          'props.required': () => {
            return (
              formBuilderService.configureFieldAction$.getValue()?.type ===
              EIremboFormlyFieldTypes.customtininput
            );
          },
        },
      },
      {
        key: EIremboFormlyValidationTypes.minSelection,
        type: EIremboFormlyFieldTypes.input,
        props: {
          placeholder: 'Enter Min Selection error message',
          label: 'Invalid Min Selection:',
          required: false,
        },
        expressions: {
          hide: (field: FormlyFieldConfig) => {
            return !field.parent?.parent?.model['minSelection'];
          },
          'props.required': (field: FormlyFieldConfig) => {
            return field.parent?.parent?.model?.['minSelection'];
          },
        },
      },
      {
        key: EIremboFormlyValidationTypes.maxSelection,
        type: EIremboFormlyFieldTypes.input,
        props: {
          placeholder: 'Enter Max Selection error message',
          label: 'Invalid Max Selection:',
          required: false,
        },
        expressions: {
          hide: (field: FormlyFieldConfig) => {
            return !field.parent?.parent?.model['maxSelection'];
          },
          'props.required': (field: FormlyFieldConfig) => {
            return field.parent?.parent?.model?.['maxSelection'];
          },
        },
      },
    ],
  };
};

export const getFileSizeValidatorFormConfig = (): FormlyFieldConfig => {
  return {
    key: 'fileSize',
    type: EIremboFormlyFieldTypes['formly-group'],
    props: {
      label: 'File Size',
      dragDisabled: true,
      required: true,
    },
    fieldGroup: [
      {
        key: 'minimumUploadSize',
        type: EIremboFormlyFieldTypes.input,
        props: {
          type: 'number',
          label: 'Minimum size (in kbs)',
          placeholder: 'Input Number',
          required: true,
          dragDisabled: true,
        },
        parsers: [Number],
        validators: {
          minSizeLessThanMaxSize: {
            expression: (c: AbstractControl) => {
              const parentMax = c.parent?.get('maximumUploadSize')?.value;

              if (!parentMax) {
                return true;
              }

              return parentMax > c.value;
            },
            message: (error: any, field: FormlyFieldConfig) =>
              `Minimum Size (${
                field?.formControl?.value
              }) should be less than Maximum Size(${
                field?.parent?.formControl?.get('maximumUploadSize')?.value
              })`,
          },
        },
      },
      {
        key: 'maximumUploadSize',
        type: EIremboFormlyFieldTypes.input,
        props: {
          type: 'number',
          label: 'Maximum size (in kbs)',
          placeholder: 'Input Number',
          required: true,
          dragDisabled: true,
        },
        parsers: [Number],
        validators: {
          maxSizeMoreThanMinSize: {
            expression: (c: AbstractControl) => {
              const parentMin = c.parent?.get('minimumUploadSize')?.value;

              if (!parentMin) {
                return true;
              }

              return c.value > parentMin;
            },
            message: (error: any, field: FormlyFieldConfig) =>
              `Maximum Size (${
                field?.formControl?.value
              }) should be more than Minimum Size(${
                field?.parent?.formControl?.get('minimumUploadSize')?.value
              })`,
          },
        },
      },
    ],
    hooks: {
      onInit: field => {
        return field?.options?.fieldChanges?.pipe(
          filter(e => {
            return (
              e.type === 'expressionChanges' &&
              (e.field.key === 'minimumUploadSize' ||
                e.field.key === 'maximumUploadSize')
            );
          }),
          tap(() => {
            field.formControl
              ?.get('minimumUploadSize')
              ?.updateValueAndValidity();
            field.formControl
              ?.get('maximumUploadSize')
              ?.updateValueAndValidity();
          })
        );
      },
    },
  };
};

export const getAllowedFileFormatsValidatorFormConfig =
  (): FormlyFieldConfig => {
    return {
      key: 'fileFormats',
      type: EIremboFormlyFieldTypes.multicheckbox,
      props: {
        label: 'File Formats',
        disabled: false,
        required: false,
        placeholder: 'Checkbox',
        options: Object.keys(AttachmentFieldFileFormat).map(key => {
          return {
            label: AttachmentFieldFileFormat[key].label,
            value: key,
          };
        }),
        multiple: false,
      },
    };
  };

export const getMinDateConfigValidatorFormConfig = (): FormlyFieldConfig => {
  return {
    key: 'minDateConfig',
    type: EIremboFormlyFieldTypes['formly-group'],
    props: {
      label: 'Minimum Date',
    },
    fieldGroup: [
      {
        key: 'minDateType',
        type: EIremboFormlyFieldTypes.radio,
        defaultValue: 'none',
        props: {
          label: 'Minimum Date Type',
          options: [
            { label: 'None', value: 'none' },
            { label: 'Relative to Today', value: 'relative' },
          ],
        },
      },
      {
        key: 'relativeMinDateValue',
        type: EIremboFormlyFieldTypes.input,
        props: {
          type: 'number',
          label: 'Number of Time Units',
          placeholder: 'Enter a number',
        },
        expressions: {
          'props.hideField': (field: FormlyFieldConfig) => {
            return field.parent?.model?.['minDateType'] !== 'relative';
          },
          'props.required': (field: FormlyFieldConfig) => {
            return field.parent?.model?.['minDateType'] === 'relative';
          },
        },
      },
      {
        key: 'relativeMinDateUnit',
        type: EIremboFormlyFieldTypes.customdropdown,
        props: {
          label: 'Time Unit',
          options: [
            { label: 'Days', value: 'd' },
            { label: 'Months', value: 'm' },
            { label: 'Years', value: 'y' },
          ],
          bindLabel: 'label',
          bindValue: 'value',
        },
        expressions: {
          'props.hideField': (field: FormlyFieldConfig) => {
            return field.parent?.model?.['minDateType'] !== 'relative';
          },
          'props.required': (field: FormlyFieldConfig) => {
            return field.parent?.model?.['minDateType'] === 'relative';
          },
        },
      },
    ],
  };
};

export const getMaxDateConfigValidatorFormConfig = (): FormlyFieldConfig => {
  return {
    key: 'maxDateConfig',
    type: EIremboFormlyFieldTypes['formly-group'],
    props: {
      label: 'Maximum Date',
    },
    fieldGroup: [
      {
        key: 'maxDateType',
        type: EIremboFormlyFieldTypes.radio,
        defaultValue: 'none',
        props: {
          label: 'Maximum Date Type',
          options: [
            { label: 'None', value: 'none' },
            { label: 'Relative to Today', value: 'relative' },
          ],
        },
      },
      {
        key: 'relativeMaxDateValue',
        type: EIremboFormlyFieldTypes.input,
        props: {
          type: 'number',
          label: 'Number of Time Units',
          placeholder: 'Enter a number',
        },
        expressions: {
          'props.hideField': (field: FormlyFieldConfig) => {
            return field.parent?.model?.['maxDateType'] !== 'relative';
          },
          'props.required': (field: FormlyFieldConfig) => {
            return field.parent?.model?.['maxDateType'] === 'relative';
          },
        },
      },
      {
        key: 'relativeMaxDateUnit',
        type: EIremboFormlyFieldTypes.customdropdown,
        props: {
          label: 'Time Unit',
          options: [
            { label: 'Days', value: 'd' },
            { label: 'Months', value: 'm' },
            { label: 'Years', value: 'y' },
          ],
          bindLabel: 'label',
          bindValue: 'value',
        },
        expressions: {
          'props.hideField': (field: FormlyFieldConfig) => {
            return field.parent?.model?.['maxDateType'] !== 'relative';
          },
          'props.required': (field: FormlyFieldConfig) => {
            return field.parent?.model?.['maxDateType'] === 'relative';
          },
        },
      },
    ],
  };
};

export const getAllowedDaysOfWeekValidatorFormConfig =
  (): FormlyFieldConfig => {
    return {
      key: 'allowedDaysOfTheWeek',
      type: EIremboFormlyFieldTypes.multicheckbox,
      props: {
        label: 'Allowed Days of Week',
        options: [
          { label: 'Sunday', value: 0 },
          { label: 'Monday', value: 1 },
          { label: 'Tuesday', value: 2 },
          { label: 'Wednesday', value: 3 },
          { label: 'Thursday', value: 4 },
          { label: 'Friday', value: 5 },
          { label: 'Saturday', value: 6 },
        ],
        description: 'Select days of the week that are allowed for selection',
      },
    };
  };
